import { COLOR_PRESETS } from '../types';
import type { UIMode } from '../context/UIModeContext';

/**
 * ColorPicker - Color selection with freemium gating
 *
 * Features:
 * - Visual color swatches
 * - Custom color picker (Pro, Advanced mode only)
 * - Feature gating UI
 * - Simple mode: 4 colors (Blue, Teal, Purple, Pink)
 * - Advanced mode: All 8 colors + custom picker
 */

interface ColorPickerProps {
  value: string;
  onChange: (color: string) => void;
  disabled?: boolean;
  isPro: boolean;
  mode: UIMode;
}

// Simple mode preset colors (4 colors)
const SIMPLE_MODE_COLORS = ['#1471ed', '#14B8A6', '#9333EA', '#EC4899'];

export function ColorPicker({
  value,
  onChange,
  disabled = false,
  isPro,
  mode,
}: ColorPickerProps): JSX.Element {
  // Filter colors based on mode
  const availableColors = mode === 'simple'
    ? COLOR_PRESETS.filter(preset => SIMPLE_MODE_COLORS.includes(preset.value))
    : COLOR_PRESETS;

  // Check if current color is a custom color (not in presets)
  const isCustomColor = !COLOR_PRESETS.some(preset => preset.value === value);

  return (
    <div>
      <div className="grid grid-cols-4 gap-2">
        {availableColors.map((preset) => {
          const isLocked = preset.isPro && !isPro;
          const isSelected = value === preset.value;

          return (
            <button
              key={preset.value}
              type="button"
              onClick={() => !isLocked && !disabled && onChange(preset.value)}
              disabled={disabled || isLocked}
              className={`
                relative w-full aspect-square rounded border-2 transition-all
                flex items-center justify-center bg-white
                ${isSelected ? 'border-gray-900 ring-2 ring-gray-900 ring-offset-1' : 'border-gray-300'}
                ${isLocked ? 'opacity-40 cursor-not-allowed' : 'hover:scale-105 cursor-pointer'}
                ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
              `}
              title={preset.name}
              aria-label={`Select ${preset.name} color`}
            >
              {/* Filled circle inside */}
              <div
                className="w-[40px] h-[40px] rounded-full"
                style={{ backgroundColor: preset.value }}
              />

              {/* Pro icon */}
              {isLocked && (
                <div className="absolute top-1 right-1 text-xs pro-icon"></div>
              )}
            </button>
          );
        })}

        {/* Custom color picker (Pro only, Advanced mode only) */}
        {mode === 'advanced' && (
          <button
            type="button"
            className={`
              relative w-full aspect-square rounded border-2 transition-all
              flex items-center justify-center bg-white
              ${isCustomColor ? 'border-gray-900 ring-2 ring-gray-900 ring-offset-1' : 'border-gray-300'}
              ${!isPro ? 'opacity-40 cursor-not-allowed' : 'hover:scale-105 cursor-pointer'}
              ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
            `}
            title="Custom color"
            disabled={disabled || !isPro}
          >
            {/* Filled circle inside showing custom color */}
            <div
              className="w-[40px] h-[40px] rounded-full relative overflow-hidden"
              style={{ backgroundColor: isCustomColor ? value : '#f3f4f6' }}
            >
              {isPro && (
                <input
                  type="color"
                  value={value}
                  onChange={(e) => onChange(e.target.value)}
                  disabled={disabled}
                  className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                  title="Custom color picker"
                />
              )}
            </div>

            {/* Artist palette icon overlay */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <span className="text-2xl drop-shadow-sm">🎨</span>
            </div>

            {/* Pro icon */}
            {!isPro && <span className="absolute top-1 right-1 text-xs pro-icon"></span>}
          </button>
        )}
      </div>

    </div>
  );
}
